import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DateUtils } from '@pbt/pbt-ui-components'

import type { Maybe, RxUsage } from '~/api/graphql/generated/types'
import i18n from '~/locales/i18n'
import {
  fetchPrescriptionWithOptions,
  getPrescriptionById,
} from '~/store/duck/prescriptions'
import type { PrescriptionV2 } from '~/types/entities/prescriptions'

import {
  EMPTY_FIELD_PLACEHOLDER,
  hasOrderTableLoadedData,
  hasRxUsages,
} from './rxCardUtilities'

const formatDeliveredDate = (dateStr: string | null | undefined): string =>
  dateStr ? DateUtils.formatDate(dateStr, false, 'MM/DD/YYYY') : ''

const trackingDisplayFor = (tracking: string | null | undefined): string => {
  const trackingNumber = tracking?.trim()
  if (!trackingNumber) return ''
  const carrier = i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FEDEX')
  return `${carrier} # ${trackingNumber}`
}

export interface RxShipment {
  createdAt?: string | null
  deliveredAt?: string | null
  estimatedDeliveryDate?: string | null
  externalShipmentId?: string | null
  shippedAt?: string | null
  trackingId?: string | null
  updatedAt?: string | null
}

export interface RxUsageWithShipments {
  dispensedAmount?: string | null
  externalRxUsageId?: string | null
  rxOrder?: {
    externalOrderId?: string | null
    placedAt?: string | null
    rxShipments?: RxShipment[] | null
  } | null
}

export interface RxUsagesWithShipmentsData {
  id: string
  rxNo?: string | null
  rxUsages?: Array<RxUsageWithShipments | null> | null
}

type PrescriptionWithId =
  | {
      id: string
      rxNo?: string | null
      rxUsages?: Maybe<Array<Maybe<RxUsage>>> | RxUsageWithShipments[] | null
    }
  | null
  | undefined

function usePrescriptionForOrderTable(
  prescription: PrescriptionWithId,
  isChewy: boolean,
  isOrderTableExpanded: boolean,
): {
  orderTableLoadedRx: PrescriptionV2 | undefined
  prescriptionForShipping: PrescriptionWithId
} {
  const dispatch = useDispatch()
  const storePrescription = useSelector(
    getPrescriptionById(prescription?.id ?? null),
  )
  const fetchRequestedRef = useRef<string | null>(null)

  useEffect(() => {
    if (
      !isOrderTableExpanded ||
      !isChewy ||
      !prescription?.id ||
      fetchRequestedRef.current === prescription.id
    ) {
      return
    }
    fetchRequestedRef.current = prescription.id
    dispatch(
      fetchPrescriptionWithOptions(prescription.id, {
        includeRefills: true,
        includeRxUsages: true,
      }),
    )
  }, [dispatch, isChewy, prescription?.id, isOrderTableExpanded])

  const hasLoadedData = Boolean(
    storePrescription && hasOrderTableLoadedData(storePrescription),
  )
  const orderTableLoadedRx = hasLoadedData
    ? (storePrescription as PrescriptionV2)
    : undefined
  const prescriptionForShipping =
    isChewy && storePrescription && hasRxUsages(storePrescription)
      ? (storePrescription as PrescriptionWithId)
      : prescription

  return { orderTableLoadedRx, prescriptionForShipping }
}

export function useRxOrderTableShipments(
  prescription: PrescriptionWithId,
  isChewy: boolean,
  isOrderTableExpanded: boolean,
): {
  data: RxUsagesWithShipmentsData | null
  orderTableLoadedRx: PrescriptionV2 | undefined
} {
  const { orderTableLoadedRx, prescriptionForShipping } =
    usePrescriptionForOrderTable(prescription, isChewy, isOrderTableExpanded)

  if (isChewy && prescriptionForShipping) {
    const rawRxUsages = prescriptionForShipping.rxUsages ?? null
    // Preserve nulls to maintain rxUsages[i] → cardHeaders[i] alignment
    const rxUsagesPreservingIndices = Array.isArray(rawRxUsages)
      ? rawRxUsages.map((usage) => usage ?? null)
      : null

    const data: RxUsagesWithShipmentsData = {
      id: prescriptionForShipping.id,
      rxNo: prescriptionForShipping.rxNo ?? null,
      rxUsages: rxUsagesPreservingIndices,
    }

    return { data, orderTableLoadedRx: orderTableLoadedRx ?? undefined }
  }

  return { data: null, orderTableLoadedRx: orderTableLoadedRx ?? undefined }
}

export function getShippingRowTexts(
  data: RxUsagesWithShipmentsData | null,
  isVetApproved: boolean,
): (string | string[])[] {
  if (!data?.rxUsages?.length || !isVetApproved) return []

  const preparingLabel = i18n.t(
    'Clients:TIMELINE.RX_STATUS_TRACKER.PREPARING_FOR_SHIPMENT',
  )
  const deliveredOnLabel = i18n.t(
    'Clients:TIMELINE.RX_STATUS_TRACKER.DELIVERED_ON',
  )

  return data.rxUsages.map((usage) => {
    if (!usage) return EMPTY_FIELD_PLACEHOLDER

    const orderId = usage.rxOrder?.externalOrderId
    if (!orderId) return EMPTY_FIELD_PLACEHOLDER

    const shipments = usage.rxOrder?.rxShipments ?? []
    const delivered = shipments
      .filter((s) => s.deliveredAt)
      .sort((a, b) =>
        (b.deliveredAt ?? '').localeCompare(a.deliveredAt ?? ''),
      )[0]

    if (!delivered) return preparingLabel

    const date = formatDeliveredDate(delivered.deliveredAt)
    const dateLine = `${deliveredOnLabel} ${date}`
    const trackingLine = trackingDisplayFor(delivered.trackingId)
    if (trackingLine) return [dateLine, trackingLine]
    return dateLine
  })
}
